import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn } from '@/styles/tailwind-utils';
import { BusinessSettingsLayout } from '@components/layout/BusinessSettingsLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Modal } from '@components/ui/Modal';
import type { Closure } from '@features/settings';
import { mapSettingsError, useClosures } from '@features/settings';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M2 4h12M6 4V2h4v2M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Closure form ─────────────────────────────────────────────────────────────

type FormValues = { name: string; date: string; isRecurring: boolean };
type ModalMode = { mode: 'add' } | { mode: 'edit'; closure: Closure };

interface ClosureFormContentProps {
  mode: 'add' | 'edit';
  closure?: Closure;
  onSubmit: (values: FormValues) => void;
  isSaving: boolean;
  saveError: string | null;
}

function ClosureFormContent({
  mode,
  closure,
  onSubmit,
  isSaving,
  saveError,
}: ClosureFormContentProps) {
  const { t } = useTranslation();
  const today = getToday();

  const schema = useMemo(() => {
    const base = z.object({
      name: z
        .string()
        .min(1, t('settings.holidays.errors.nameRequired'))
        .max(100, t('settings.holidays.errors.nameMax')),
      date: z.string().min(1, t('settings.holidays.errors.dateRequired')),
      isRecurring: z.boolean(),
    });
    if (mode === 'add') {
      return base.refine((d) => d.date >= today, {
        message: t('settings.holidays.errors.datePast'),
        path: ['date'],
      });
    }
    return base;
  }, [t, mode, today]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: closure?.name ?? '',
      date: closure?.date ?? today,
      isRecurring: closure?.isRecurring ?? false,
    },
  });

  const isRecurring = watch('isRecurring');

  return (
    <form id="closure-form" onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      {saveError && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {saveError}
        </div>
      )}

      <Input
        label={t('settings.holidays.modal.name')}
        placeholder={t('settings.holidays.modal.namePlaceholder')}
        error={errors.name?.message}
        disabled={isSaving}
        {...register('name')}
      />

      <Input
        label={t('settings.holidays.modal.date')}
        type="date"
        min={mode === 'add' ? today : undefined}
        error={errors.date?.message}
        disabled={isSaving}
        {...register('date')}
      />

      <div className="flex items-center justify-between py-1">
        <span className="text-sm font-medium text-foreground">
          {t('settings.holidays.modal.isRecurring')}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={isRecurring}
          onClick={() => setValue('isRecurring', !isRecurring, { shouldValidate: true })}
          disabled={isSaving}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
            'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            isRecurring ? 'bg-brand' : 'bg-border',
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200',
              isRecurring ? 'translate-x-4' : 'translate-x-0',
            )}
          />
        </button>
      </div>
    </form>
  );
}

interface ClosureFormModalProps {
  modalMode: ModalMode | null;
  onClose: () => void;
  onSave: (values: FormValues, closureId?: string) => void;
  isSaving: boolean;
  saveError: string | null;
}

function ClosureFormModal({
  modalMode,
  onClose,
  onSave,
  isSaving,
  saveError,
}: ClosureFormModalProps) {
  const { t } = useTranslation();
  const isEditing = modalMode?.mode === 'edit';

  return (
    <Modal
      isOpen={!!modalMode}
      onClose={onClose}
      title={
        isEditing
          ? t('settings.holidays.modal.editTitle')
          : t('settings.holidays.modal.addTitle')
      }
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={isSaving}>
            {t('settings.holidays.modal.cancel')}
          </Button>
          <Button type="submit" form="closure-form" isLoading={isSaving}>
            {isEditing
              ? t('settings.holidays.modal.save')
              : t('settings.holidays.modal.add')}
          </Button>
        </>
      }
    >
      {modalMode && (
        <ClosureFormContent
          key={modalMode.mode === 'edit' ? modalMode.closure.closureId : '__add__'}
          mode={modalMode.mode}
          closure={modalMode.mode === 'edit' ? modalMode.closure : undefined}
          onSubmit={(values) =>
            onSave(
              values,
              modalMode.mode === 'edit' ? modalMode.closure.closureId : undefined,
            )
          }
          isSaving={isSaving}
          saveError={saveError}
        />
      )}
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function HolidaysPage() {
  const { t } = useTranslation();
  const [modalMode, setModalMode] = useState<ModalMode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Closure | null>(null);

  const {
    closures,
    isLoading,
    fetchError,
    create,
    isCreating,
    createError,
    patch,
    isPatching,
    patchError,
    remove,
    isDeleting,
    deleteError,
  } = useClosures();

  const isSaving = isCreating || isPatching;
  const activeError = modalMode?.mode === 'add' ? createError : patchError;
  const saveError = activeError
    ? mapSettingsError(activeError, t, 'settings.holidays.errors.saveFailed')
    : null;

  function handleSave(values: FormValues, closureId?: string) {
    if (closureId) {
      patch(
        { closureId, body: { name: values.name, date: values.date, isRecurring: values.isRecurring } },
        { onSuccess: () => setModalMode(null) },
      );
    } else {
      create(
        { name: values.name, date: values.date, isRecurring: values.isRecurring },
        { onSuccess: () => setModalMode(null) },
      );
    }
  }

  function handleDelete() {
    if (!deleteTarget) return;
    remove(deleteTarget.closureId, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  const sorted = [...closures].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <BusinessSettingsLayout>
      {isLoading ? (
        <BrandLoader
          title={t('settings.loading.title')}
          subtitle={t('settings.loading.subtitle')}
        />
      ) : fetchError ? (
        <div className="rounded-md border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-destructive">
            {mapSettingsError(fetchError, t, 'settings.holidays.errors.loadFailed')}
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-surface">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('settings.holidays.title')}
            </h2>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<PlusIcon />}
              onClick={() => setModalMode({ mode: 'add' })}
            >
              {t('settings.holidays.addClosure')}
            </Button>
          </div>

          {/* List */}
          <div className="px-4 py-5 sm:px-6 sm:py-6">
            {sorted.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('settings.holidays.noClosures')}
              </p>
            ) : (
              <div className="space-y-2">
                {sorted.map((closure) => (
                  <div
                    key={closure.closureId}
                    className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-surface px-4 py-3"
                  >
                    <span className="w-24 shrink-0 text-sm tabular-nums text-muted-foreground sm:w-28">
                      {formatDate(closure.date)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                      {closure.name}
                    </span>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium',
                        closure.isRecurring
                          ? 'bg-brand/10 text-brand'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {closure.isRecurring
                        ? t('settings.holidays.recurring')
                        : t('settings.holidays.oneTime')}
                    </span>
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        aria-label={t('settings.holidays.editAria', { name: closure.name })}
                        onClick={() => setModalMode({ mode: 'edit', closure })}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        aria-label={t('settings.holidays.deleteAria', { name: closure.name })}
                        onClick={() => setDeleteTarget(closure)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ClosureFormModal
        modalMode={modalMode}
        onClose={() => setModalMode(null)}
        onSave={handleSave}
        isSaving={isSaving}
        saveError={saveError}
      />

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('settings.holidays.confirmDelete.title')}
        footer={
          <>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              {t('settings.holidays.confirmDelete.cancel')}
            </Button>
            <Button variant="danger" type="button" onClick={handleDelete} isLoading={isDeleting}>
              {t('settings.holidays.confirmDelete.confirm')}
            </Button>
          </>
        }
      >
        <p>{t('settings.holidays.confirmDelete.message', { name: deleteTarget?.name })}</p>
        {deleteError && (
          <p className="mt-3 text-sm text-destructive">
            {mapSettingsError(deleteError, t, 'settings.holidays.errors.deleteFailed')}
          </p>
        )}
      </Modal>
    </BusinessSettingsLayout>
  );
}
